import React from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row } from "react-bootstrap";

import testIds from "constants/testIds";
import Button from "components/atoms/Button/Button";
import FormField from "components/molecules/FormField/FormField";
import useAddressV2Fields from "hooks/useAddressV2Fields";

type BillingInfoAddressV2Props = {
  onSubmit: () => void;
  onClose: () => void;
};

function BillingInfoAddressV2(props: BillingInfoAddressV2Props) {
  const { onSubmit, onClose } = props;

  const intl = useIntl();
  const methods = useFormContext();
  const { control, watch, setValue, trigger } = methods;

  const { fields: addressV2BilledFromFields = [] } = useAddressV2Fields({
    viewOnly: false,
    control,
    watch,
    setValue,
    enable_new_address_structure: true,
    useV2: true,
    fieldNames: {
      countryId: "invoice.billed_from.country_id",
      cityId: "invoice.billed_from.city_id",
      stateId: "invoice.billed_from.state_id",
      stateName: "invoice.billed_from.state",
      cityName: "invoice.billed_from.city",
      countryName: "invoice.billed_from.country",
    },
  });

  const { fields: addressV2BilledToFields = [] } = useAddressV2Fields({
    viewOnly: false,
    control,
    watch,
    setValue,
    enable_new_address_structure: true,
    useV2: true,
    fieldNames: {
      countryId: "invoice.billed_to.country_id",
      cityId: "invoice.billed_to.city_id",
      stateId: "invoice.billed_to.state_id",
      stateName: "invoice.billed_to.state",
      cityName: "invoice.billed_to.city",
      countryName: "invoice.billed_to.country",
    },
  });

  const billedFromCountryAndStateField = addressV2BilledFromFields.slice(0, 2);
  const billedToCountryAndStateField = addressV2BilledToFields.slice(0, 2);
  const billedFromCityField = addressV2BilledFromFields.slice(2)?.[0];
  const billedToCityField = addressV2BilledToFields.slice(2)?.[0];

  const handleSubmit = async () => {
    const isValid = await trigger(["invoice.billed_from", "invoice.billed_to"]);
    if (!isValid) {
      return;
    }
    onSubmit();
  };

  return (
    <div>
      <Row>
        <Col md={6} lg={6}>
          <h6>
            <FormattedMessage id="billing.billedFrom" defaultMessage="Billed from " />:
          </h6>
          <div className="w-192 mt-3">
            <FormField
              label={intl.formatMessage({
                id: "general.companyAndAddress",
                defaultMessage: "Company/Address",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_from.company"
              mandatory
            />
          </div>
          <div className="w-192 mt-4">
            <FormField
              label={intl.formatMessage({
                id: "general.address",
                defaultMessage: "Address",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_from.address"
              mandatory
            />
          </div>
          {billedFromCountryAndStateField.map((field, index) => (
            <div className="w-192 mt-4" key={index}>
              <span className={`${field?.required ? "required" : ""} d-flex`}>{field?.label}</span>
              {field?.children}
            </div>
          ))}
          <div className="w-192 mt-4">
            <span className={`${billedFromCityField?.required ? "required" : ""} d-flex`}>{billedFromCityField?.label}</span>
            {React.cloneElement(billedFromCityField?.children, {
              enableManualValue: true,
              allowNew: true,
              newSelectionPrefix: "",
              manualName: "invoice.billed_from.city",
              preselect: false,
            })}
          </div>
        </Col>
        <Col md={6} lg={6}>
          <h6>
            <FormattedMessage id="billing.billedTo" defaultMessage="Billed to:" />
          </h6>
          <div className="w-192 mt-3">
            <FormField
              label={intl.formatMessage({
                id: "general.companyAndAddress",
                defaultMessage: "Company/Address",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_to.company"
              mandatory
            />
          </div>
          <div className="w-192 mt-4">
            <FormField
              label={intl.formatMessage({
                id: "general.address",
                defaultMessage: "Address",
              })}
              type="textinput"
              placeholder={intl.formatMessage({
                id: "general.typeHere",
                defaultMessage: "Type here..",
              })}
              field_id="invoice.billed_to.address"
              mandatory
            />
          </div>
          {billedToCountryAndStateField.map((field, index) => (
            <div className="w-192 mt-4" key={index}>
              <span className={`${field?.required ? "required" : ""} d-flex`}>{field?.label}</span>
              {field?.children}
            </div>
          ))}
          <div className="w-192 mt-4">
            <span className={`${billedToCityField?.required ? "required" : ""} d-flex`}>{billedToCityField?.label}</span>
            {React.cloneElement(billedToCityField?.children, {
              enableManualValue: true,
              allowNew: true,
              newSelectionPrefix: "",
              manualName: "invoice.billed_to.city",
              preselect: false,
            })}
          </div>
        </Col>
      </Row>
      <Row className="mt-5">
        <Col lg={12} className="d-flex justify-content-between">
          <Button
            variant="outline-primary"
            label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
            size="lg"
            onClick={onClose}
            data-testid={testIds.SHIPMENT_DASHBOARD_UNDECLINE_MODAL_CLOSE_BUTTON}
          />
          <Button
            variant="primary"
            type="button"
            size="lg"
            label={intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
            data-testid={testIds.SHIPMENT_DASHBOARD_UNDECLINE_UNDO_BUTTON}
            onClick={handleSubmit}
          />
        </Col>
      </Row>
    </div>
  );
}

export default BillingInfoAddressV2;
